import React, {useState, useEffect, useCallback, useRef} from 'react'
import {unstable_batchedUpdates as batchedUpdates} from 'react-dom'
import {Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import SearchBar from 'src/components-new/inputs/SearchBar'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'

interface ParticipantLookupDialogNewProps {
  mode: 'patient' | 'doctor'
  onClose: () => void
  onRowClick: (row: Record<string, any>) => void
  customFilters?: Record<string, string | string[]>[]
}

const PATIENT_COLUMNS: ColumnDef<Record<string, any>>[] = [
  {
    key: 'firstName',
    header: 'Nome',
    render: (row) => <Typography variant="body2">{row.firstName ?? '—'}</Typography>,
  },
  {
    key: 'lastName',
    header: 'Cognome',
    render: (row) => <Typography variant="body2">{row.lastName ?? '—'}</Typography>,
  },
  {
    key: 'taxCode',
    header: 'Codice fiscale',
    render: (row) => <Typography variant="body2">{row.taxCode ?? '—'}</Typography>,
  },
]

const DOCTOR_COLUMNS: ColumnDef<Record<string, any>>[] = [
  {
    key: 'firstName',
    header: 'Nome',
    render: (row) => <Typography variant="body2">{row.firstName ?? '—'}</Typography>,
  },
  {
    key: 'lastName',
    header: 'Cognome',
    render: (row) => <Typography variant="body2">{row.lastName ?? '—'}</Typography>,
  },
  {
    key: 'specialization',
    header: 'Specializzazione',
    render: (row) => <Typography variant="body2">{row.specialization ?? '—'}</Typography>,
  },
]

const DEFAULT_PAGINATION: PaginationState = {page: 0, rowsPerPage: 10}

const EMPTY_FILTERS: Record<string, string | string[]>[] = []

export default function ParticipantLookupDialogNew({
  mode,
  onClose,
  onRowClick,
  customFilters = EMPTY_FILTERS,
}: ParticipantLookupDialogNewProps): JSX.Element {
  const [search, setSearch] = useState('')
  const [data, setData] = useState<Record<string, any>[]>([])
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  const buildPatientFilters = useCallback(
    (searchValue: string): Record<string, any>[] => {
      if (!searchValue.trim()) return []
      return [{property: 'name', type: 'text', value: searchValue.trim()}]
    },
    []
  )

  const buildDoctorFilters = useCallback(
    (searchValue: string): Record<string, any>[] => {
      const searchFilters: Record<string, any>[] = []
      if (searchValue.trim()) {
        searchFilters.push({property: 'firstName', type: 'text', value: searchValue.trim()})
      }
      return [...customFilters, ...searchFilters]
    },
    [customFilters]
  )

  const fetchData = useCallback(async () => {
    if (mode === 'patient') {
      const filters = buildPatientFilters(search)
      const result = await apiClient.postPatientsSearch(filters, setStatus, {
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        order: [],
      })
      setData(result ?? [])
      const countResult = await apiClient.postPatientsSearchCount(filters, setStatus)
      setCount(Number(countResult) ?? 0)
    } else {
      const filters = buildDoctorFilters(search)
      const result = await apiClient.getDoctorsSearch(filters, setStatus, {
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        order: [],
      })
      setData(result ?? [])
      const countResult = await apiClient.getDoctorsCount(filters, setStatus)
      setCount(Number(countResult) ?? 0)
    }
  }, [mode, search, pagination, buildPatientFilters, buildDoctorFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearchChange = (value: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      batchedUpdates(() => {
        setSearch(value)
        setPagination((p) => ({...p, page: 0}))
      })
    }, 300)
  }

  const columns = mode === 'patient' ? PATIENT_COLUMNS : DOCTOR_COLUMNS
  const title = mode === 'patient' ? 'Cerca paziente' : 'Cerca medico'
  const placeholder = mode === 'patient' ? 'Cerca per nome o cognome...' : 'Cerca medico...'
  const emptyMessage = mode === 'patient' ? 'Nessun paziente trovato' : 'Nessun medico trovato'

  return (
    <AppDialog
      maxWidth="md"
      onClose={onClose}
      open={true}
      title={title}
    >
      <SearchBar
        fullWidth
        value={search}
        onChange={handleSearchChange}
        placeholder={placeholder}
        sx={{mb: 2}}
      />
      <DataTable<Record<string, any>>
        columns={columns}
        emptyMessage={emptyMessage}
        keyField="_id"
        loading={status === STATUS.PENDING}
        onPaginationChange={setPagination}
        onRowClick={onRowClick}
        pagination={pagination}
        rows={data}
        totalElements={count}
      />
    </AppDialog>
  )
}
